import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  MarkerType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ApiService } from '../services/api';
import { AreaDetail } from '../types';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { ArrowLeft, Users, Briefcase, User, Settings, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import PersonalCard from './PersonalCard';
import DevOpsCapacityCard from './DevOpsCapacityCard';

// Componente para el nodo central del área
const AreaCentralNode = ({ data }: { data: any }) => (
  <div className="relative group">
    {/* Handle de salida en la parte inferior */}
    <Handle
      type="source"
      position={Position.Bottom}
      id="area-bottom"
      style={{ background: '#3B82F6', width: '12px', height: '12px', border: '2px solid white' }}
    />

    <div className="relative px-10 py-8 shadow-lg rounded-2xl bg-primary/10 dark:bg-primary/20 border-2 border-primary hover:shadow-xl transition-all duration-300"
         style={{ width: '280px', height: '160px' }}>

      <div className="text-center flex flex-col justify-center h-full">
        <div className="text-neutral-900 dark:text-neutral-50 font-bold text-2xl mb-3 leading-tight">{data.label}</div>
        <div className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 leading-relaxed">{data.description}</div>

        <div className="flex justify-center space-x-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">{data.serviciosCount}</div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Servicios</div>
          </div>
          <div className="w-px bg-neutral-300 dark:bg-neutral-600"></div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">{data.personalCount}</div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Personal</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Componente para nodos de servicio
const ServicioNode = ({ data }: { data: any }) => (
  <div className="relative group">
    {/* Handle de entrada en la parte superior */}
    <Handle
      type="target"
      position={Position.Top}
      id="servicio-top"
      style={{ background: '#6B7280', width: '10px', height: '10px', border: '2px solid white' }}
    />

    {/* Handle de salida en la parte inferior */}
    <Handle
      type="source"
      position={Position.Bottom}
      id="servicio-bottom"
      style={{ background: '#6B7280', width: '10px', height: '10px', border: '2px solid white' }}
    />

    <div className="card px-5 py-4 cursor-pointer hover:-translate-y-1 transition-all duration-300"
         style={{ width: '200px', minHeight: '100px' }}>

      {/* Header with icon */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
          <Briefcase className="text-neutral-600 dark:text-neutral-400" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-neutral-900 dark:text-neutral-50 text-sm leading-tight">{data.label}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-2">
        {data.categoria && (
          <div className="flex">
            <span className="badge text-xs">
              {data.categoria}
            </span>
          </div>
        )}

        {data.casosUso && (
          <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed line-clamp-3">
            {data.casosUso}
          </p>
        )}
      </div>
    </div>

    {/* Tooltip simplificado */}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
      <div className="px-4 py-2 bg-neutral-900 dark:bg-neutral-800 text-white text-sm rounded-lg shadow-xl border border-neutral-700">
        <div className="font-medium">{data.label}</div>
        {data.categoria && <div className="text-xs text-neutral-300 mt-1">{data.categoria}</div>}
      </div>
    </div>
  </div>
);

// Componente para nodos de personal
const PersonalNode = ({ data }: { data: any }) => (
  <div className="relative group">
    {/* Handle de entrada en la parte superior */}
    <Handle
      type="target"
      position={Position.Top}
      id="personal-top"
      style={{ background: '#9CA3AF', width: '10px', height: '10px', border: '2px solid white' }}
    />

    {/* Handle de salida en la parte inferior */}
    <Handle
      type="source"
      position={Position.Bottom}
      id="personal-bottom"
      style={{ background: '#9CA3AF', width: '10px', height: '10px', border: '2px solid white' }}
    />

    <div className="card px-4 py-4 hover:-translate-y-1 transition-all duration-300"
         style={{ width: '180px', minHeight: '90px' }}>

      {/* Header */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="relative">
          <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-full flex items-center justify-center">
            <User className="text-neutral-600 dark:text-neutral-400" size={16} />
          </div>
          {/* Status indicator */}
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-neutral-900 shadow-sm ${
            data.disponible ? 'bg-success' : 'bg-error'
          }`}></div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-neutral-900 dark:text-neutral-50 text-sm leading-tight">{data.label}</h3>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 font-medium">{data.rol}</p>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center">
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
          data.disponible
            ? 'bg-success/10 dark:bg-success/20 text-success border-success/30'
            : 'bg-error/10 dark:bg-error/20 text-error border-error/30'
        }`}>
          {data.disponible ? 'Disponible' : 'Ocupado'}
        </span>
      </div>
    </div>

    {/* Tooltip simplificado */}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
      <div className="px-4 py-2 bg-neutral-900 dark:bg-neutral-800 text-white text-sm rounded-lg shadow-xl border border-neutral-700 min-w-max">
        <div className="font-medium">{data.label}</div>
        <div className="text-xs text-neutral-300 mt-1">{data.rol}</div>
        {data.email && <div className="text-xs text-neutral-400 mt-1">{data.email}</div>}
      </div>
    </div>
  </div>
);

const nodeTypes = {
  area: AreaCentralNode,
  servicio: ServicioNode,
  personal: PersonalNode,
};

const AreaOrganigrama: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [areaDetail, setAreaDetail] = useState<AreaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para ReactFlow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const areaId = id;

  // Función para generar organigrama jerárquico solo con personal
  const generateOrganigramaData = useMemo(() => {
    if (!areaDetail) return { nodes: [], edges: [] };

    const orgNodes: Node[] = [];
    const orgEdges: Edge[] = [];

    // Organizar personal por jerarquía basada en roles (sin nodo central)
    if (areaDetail.personal.length > 0) {
      // Definir jerarquía de roles (de mayor a menor)
      const jerarquiaRoles = [
        'head of', 'director', 'gerente', 'manager', 'jefe', 'team leader', 'lead', 'coordinator',
        'senior', 'ssr', 'semi-senior', 'consultor', 'jr', 'junior', 'intern',
        'consultant', 'specialist', 'analyst', 'developer', 'engineer'
      ];

      // Función para determinar el nivel jerárquico
      const getNivelJerarquico = (rol: string): number => {
        const rolLower = rol.toLowerCase();
        
        // Buscar palabras clave en el rol
        for (let i = 0; i < jerarquiaRoles.length; i++) {
          if (rolLower.includes(jerarquiaRoles[i])) {
            return Math.floor(i / 3); // Agrupar cada 3 roles en un nivel
          }
        }
        return 2; // Nivel por defecto
      };

      // Agrupar personal por nivel jerárquico
      const personalPorNivel: { [nivel: number]: typeof areaDetail.personal } = {};
      
      areaDetail.personal.forEach(persona => {
        const nivel = getNivelJerarquico(persona.rol);
        if (!personalPorNivel[nivel]) {
          personalPorNivel[nivel] = [];
        }
        personalPorNivel[nivel].push(persona);
      });

      // Ordenar niveles
      const nivelesOrdenados = Object.keys(personalPorNivel)
        .map(n => parseInt(n))
        .sort((a, b) => a - b);

      // Posicionar personal por niveles (sin nodo central)
      nivelesOrdenados.forEach((nivel, nivelIndex) => {
        const personalEnNivel = personalPorNivel[nivel];
        const yPosition = 50 + (nivelIndex * 220); // Comenzar desde arriba sin nodo central
        const personalSpacing = Math.min(320, 1000 / personalEnNivel.length); // Espaciado horizontal aumentado
        const startX = 400 - ((personalEnNivel.length - 1) * personalSpacing) / 2;

        personalEnNivel.forEach((persona, personaIndex) => {
          const x = startX + (personaIndex * personalSpacing);
          const y = yPosition;

          orgNodes.push({
            id: `personal-${persona.id}`,
            type: 'personal',
            position: { x: x - 90, y: y - 45 },
            data: {
              label: `${persona.nombre} ${persona.apellido}`,
              rol: persona.rol.toLowerCase().includes('head of') ? `Head of ${areaDetail.nombre}` : persona.rol,
              disponible: persona.disponible,
              email: persona.email,
            },
            draggable: false,
          });

          // Conexiones jerárquicas (solo entre niveles, sin nodo central)
          if (nivel > 0) {
            // Niveles inferiores se conectan al nivel superior más cercano
            const nivelSuperior = nivelesOrdenados[nivelIndex - 1];
            const personalNivelSuperior = personalPorNivel[nivelSuperior];

            // Validar que existe personal en el nivel superior
            if (personalNivelSuperior && personalNivelSuperior.length > 0) {
              // Conectar con la persona más cercana del nivel superior
              const personaSuperiorIndex = Math.floor(personaIndex * personalNivelSuperior.length / personalEnNivel.length);
              const personaSuperior = personalNivelSuperior[Math.min(personaSuperiorIndex, personalNivelSuperior.length - 1)];

              orgEdges.push({
                id: `jerarquia-${personaSuperior.id}-${persona.id}`,
                source: `personal-${personaSuperior.id}`,
                sourceHandle: 'personal-bottom',
                target: `personal-${persona.id}`,
                targetHandle: 'personal-top',
                type: 'straight',
                animated: false,
                style: {
                  stroke: '#9CA3AF',
                  strokeWidth: 2,
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#9CA3AF',
                },
              });
            }
          }
          // Nivel 0 (top-level) no tiene conexiones entrantes
        });
      });
    }

    return { nodes: orgNodes, edges: orgEdges };
  }, [areaDetail]);

  useEffect(() => {
    if (areaId) {
      fetchAreaDetail();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId]);

  // Actualizar nodos y conexiones cuando cambien los datos
  useEffect(() => {
    if (areaDetail) {
      const { nodes: newNodes, edges: newEdges } = generateOrganigramaData;
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [areaDetail, generateOrganigramaData, setNodes, setEdges]);

  const fetchAreaDetail = async () => {
    if (!areaId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getAreaById(parseInt(areaId));
      setAreaDetail(response);
    } catch (err) {
      console.error('Error al obtener detalle del área:', err);
      setError('Error al cargar la información del área');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    // Navegar de vuelta al dashboard
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button 
            onClick={handleGoBack}
            className="mb-6 flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <ArrowLeft size={20} />
            <span>Volver</span>
          </button>
          <ErrorMessage message={error} onRetry={fetchAreaDetail} />
        </div>
      </div>
    );
  }

  if (!areaDetail) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-4">Área no encontrada</h2>
          <button 
            onClick={handleGoBack}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors mx-auto"
          >
            <ArrowLeft size={20} />
            <span>Volver</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 shadow-lg border-b border-neutral-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGoBack}
                className="flex items-center space-x-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
              >
                <ArrowLeft size={20} className="text-neutral-600 dark:text-neutral-400" />
                <span className="text-neutral-900 dark:text-neutral-50">Volver</span>
              </button>

              <div>
                <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                  Organigrama: {areaDetail.nombre}
                </h1>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                  {areaDetail.descripcion || 'Estructura organizacional del área'}
                </p>
              </div>
            </div>

            {/* Estadísticas rápidas */}
            <div className="flex space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{areaDetail.servicios.length}</div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Servicios</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{areaDetail.personal.length}</div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Personal</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organigrama Visual Full Screen */}
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header compacto */}
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2 flex items-center justify-center">
              <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-lg flex items-center justify-center mr-4">
                <Settings className="text-neutral-600 dark:text-neutral-400" size={24} />
              </div>
              Organigrama: {areaDetail.nombre}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400">{areaDetail.descripcion || 'Estructura organizacional del área'}</p>
          </div>
          
          {/* Organigrama principal - pantalla completa */}
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden shadow-lg" style={{ height: '80vh', minHeight: '600px' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
              panOnDrag={true}
              zoomOnScroll={true}
              minZoom={0.2}
              maxZoom={1.5}
              defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={30}
                size={2}
                color="#D1D5DB"
                className="dark:opacity-20"
              />
              <Controls
                position="bottom-right"
                showInteractive={false}
                className="bg-white dark:bg-neutral-800 shadow-lg rounded-lg border border-neutral-200 dark:border-neutral-700 m-4"
              />
            </ReactFlow>
          </div>
          
          {/* Leyenda minimalista en la parte inferior */}
          <div className="mt-6 card p-4">
            <div className="flex items-center justify-center space-x-8 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-primary/20 border-2 border-primary rounded-lg"></div>
                <span className="font-medium text-neutral-700 dark:text-neutral-300">Área Principal</span>
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-neutral-200 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-full"></div>
                <span className="font-medium text-neutral-700 dark:text-neutral-300">Personal ({areaDetail.personal.length})</span>
              </div>

              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-0.5 bg-primary"></div>
                  <div className="text-xs text-neutral-600 dark:text-neutral-400 ml-1">Directo</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-0.5 bg-neutral-400"></div>
                  <div className="text-xs text-neutral-600 dark:text-neutral-400 ml-1">Jerárquico</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="text-xs text-neutral-600 dark:text-neutral-400">
                  📊 Organigrama Jerárquico • 🔍 Zoom • 🖱️ Arrastrar
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resumen de Capacidad del Personal */}
        <div className="mt-8 max-w-7xl mx-auto px-4">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2 flex items-center">
              <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-lg flex items-center justify-center mr-4">
                <TrendingUp className="text-neutral-600 dark:text-neutral-400" size={24} />
              </div>
              Capacidad del Equipo
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">Resumen detallado de disponibilidad y carga de trabajo del personal</p>
          </div>

          {/* Estadísticas generales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-success/10 dark:bg-success/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-success" size={20} />
                </div>
                <div className="text-2xl font-bold text-success">
                  {areaDetail.personal.filter(p => p.disponible).length}
                </div>
              </div>
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Personal Disponible</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {((areaDetail.personal.filter(p => p.disponible).length / areaDetail.personal.length) * 100).toFixed(0)}% del equipo
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-error/10 dark:bg-error/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="text-error" size={20} />
                </div>
                <div className="text-2xl font-bold text-error">
                  {areaDetail.personal.filter(p => !p.disponible).length}
                </div>
              </div>
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Personal Ocupado</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {((areaDetail.personal.filter(p => !p.disponible).length / areaDetail.personal.length) * 100).toFixed(0)}% del equipo
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                  <Users className="text-primary" size={20} />
                </div>
                <div className="text-2xl font-bold text-primary">
                  {areaDetail.personal.length}
                </div>
              </div>
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Total Personal</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                {areaDetail.servicios.length} servicios cubiertos
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-neutral-200 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                  <Briefcase className="text-neutral-600 dark:text-neutral-400" size={20} />
                </div>
                <div className="text-2xl font-bold text-neutral-700 dark:text-neutral-300">
                  {Math.round((areaDetail.personal.filter(p => p.disponible).length / areaDetail.servicios.length) * 100) / 100 || 0}
                </div>
              </div>
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-50">Ratio Personal/Servicio</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Promedio por servicio
              </div>
            </div>
          </div>

          {/* Cards individuales del personal con fotos de Graph y DevOps capacity */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {areaDetail.personal.map((persona) => (
              <div key={persona.id} className="space-y-4">
                <PersonalCard
                  personal={persona}
                  layoutMode="grid"
                />
                {persona.email && (
                  <div className="card p-4">
                    <DevOpsCapacityCard
                      userEmail={persona.email}
                      userName={`${persona.nombre} ${persona.apellido || ''}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaOrganigrama;